import type { RootState } from '@/stores/store'
import type { HubReadSource } from '@/types/hub-fallback'

import { createSlice, PayloadAction } from '@reduxjs/toolkit'

// Tracks the freshness of hub-direct READ data per hub AND per route family
// ('guide', 'video', 'pms', 'server', …), so banners and write-gating can
// react. Filled by the RTK Query base query on every /api/hub/<ip>/<family>
// response that declares its source.
//
// Per FAMILY, not per IP, because an atlas-service can be broken in one place
// and healthy everywhere else. S6702 is the case that forced this: it serves
// `guide/lineups` live and 500s on `server/config`. Keyed per IP, that one
// broken endpoint marked the whole hub stale and made the Guide editor
// read-only while it was showing live, editable data — the fallback machinery
// reporting a hub as down on the strength of a page nobody had opened.
type HubStatus = {
  source: HubReadSource
  staleAt: string | null
  hubError: string | null
  // When we last observed the hub for this family.
  observedAt: string
}

type HubStatusState = {
  byResource: Record<string, HubStatus>
}

const initialState: HubStatusState = {
  byResource: {}
}

/** The store key. `|` cannot appear in an IP or in a route segment. */
const resourceKey = (ip: string, resource: string) => `${ip}|${resource}`

const hubStatusSlice = createSlice({
  name: 'hubStatus',
  initialState,
  reducers: {
    recordHubStatus(
      state,
      action: PayloadAction<{
        ip: string
        /** First path segment after the IP — the hub route family. */
        resource: string
        source: HubReadSource
        staleAt: string | null
        hubError: string | null
      }>
    ) {
      const { ip, resource, source, staleAt, hubError } = action.payload
      state.byResource[resourceKey(ip, resource)] = {
        source,
        staleAt,
        hubError,
        observedAt: new Date().toISOString()
      }
    },
    clearHubStatus(state, action: PayloadAction<string>) {
      const prefix = `${action.payload}|`
      for (const key of Object.keys(state.byResource)) {
        if (key.startsWith(prefix)) delete state.byResource[key]
      }
    }
  }
})

export const { recordHubStatus, clearHubStatus } = hubStatusSlice.actions

export const hubStatusReducer = hubStatusSlice.reducer

/**
 * The status for one family, or — without one — the most recently observed
 * status on the hub.
 *
 * The resource-less form exists for the generic wrappers (the stale banner,
 * `HubGatedConfiguration`) that are handed an IP and no page context. It is
 * deliberately "newest", not "any stale": a caller that cannot say which data
 * it is showing gets the hub's latest word rather than a verdict assembled
 * from whichever page happened to load first.
 */
export const selectHubStatus = (
  state: RootState,
  ip: string,
  resource?: string
) => {
  const byResource = state.hubStatus?.byResource
  if (!byResource) return undefined
  if (resource) return byResource[resourceKey(ip, resource)]

  const prefix = `${ip}|`
  let newest: HubStatus | undefined
  for (const [key, status] of Object.entries(byResource)) {
    if (!key.startsWith(prefix)) continue
    if (!newest || status.observedAt > newest.observedAt) newest = status
  }
  return newest
}

export const selectIsHubStale = (
  state: RootState,
  ip: string,
  resource?: string
) => selectHubStatus(state, ip, resource)?.source === 'postgres-fallback'
